/**
 * Formats a date to a human-readable string
 * @param date Date to format
 * @returns Formatted date string
 */
export function formatDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/**
 * Formats a date range to a human-readable string
 * @param startDate Start date
 * @param endDate End date
 * @returns Formatted date range string
 */
export function formatDateRange(startDate: string | Date, endDate: string | Date): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate
  
  // If dates are on the same day
  if (start.toDateString() === end.toDateString()) {
    return formatDate(start)
  }
  
  // If dates are in the same month and year
  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.toLocaleDateString('en-US', { day: 'numeric' })} - ${formatDate(end)}`
  }
  
  // Different months or years
  return `${formatDate(start)} - ${formatDate(end)}`
}

/**
 * Formats a time to a human-readable string
 * @param time Time to format
 * @returns Formatted time string
 */
export function formatTime(time: string | Date): string {
  const timeObj = typeof time === 'string' ? new Date(time) : time
  return timeObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Formats a date and time to a human-readable string
 * @param datetime Date and time to format
 * @returns Formatted date and time string
 */
export function formatDateTime(datetime: string | Date): string {
  const datetimeObj = typeof datetime === 'string' ? new Date(datetime) : datetime
  return `${formatDate(datetimeObj)} at ${formatTime(datetimeObj)}`
}
